import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { CreateWorkflowRequest } from 'src/app/core-module/models/Events/create-workflow-request';
import { CreateWorkflowActionRequest } from 'src/app/core-module/models/Events/create-workflow-action-request';
import { UpdateWorkflowActionRequest } from 'src/app/core-module/models/Events/update-workflow-action-request';
import { WorkflowActionTemplate } from 'src/app/core-module/models/Events/workflow-action-template';
import { WorkflowTemplate } from 'src/app/core-module/models/Events/workflow-template';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { WorkflowService } from 'src/app/core-module/services/workflow.service';
import { SharedActionBarComponent } from '../../shared-action-bar/shared-action-bar.component';
import { RouterModule } from '@angular/router';
import { ComplianceActionService } from 'src/app/core-module/services/compliance-action.service';
import { ActionUserRequest } from 'src/app/core-module/models/Compliance-Actions/action-user-request';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { UserService } from 'src/app/core-module/services/user.service';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-workflows',
  templateUrl: './workflows.component.html',
  styleUrls: ['./workflows.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SelectModule, ConfirmPopupModule, SharedActionBarComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService]
})
export class WorkflowsComponent {
  private readonly customerContext = inject(CustomerContextService);

  workflows: WorkflowTemplate[] = [];
  loading: boolean = false;
  saving: boolean = false;
  errorMessage: string = '';
  formError: string = '';
  newWorkflowName: string = '';
  newWorkflowDescription: string = '';
  expandedWorkflowId: number = -1;
  actions: Record<number, WorkflowActionTemplate[]> = {};
  actionsLoading: Record<number, boolean> = {};
  actionsError: Record<number, string> = {};
  actionForms: Record<number, CreateWorkflowActionRequest> = {};
  actionSaving: Record<number, boolean> = {};
  actionAssignees: Record<number, BasicUser | null> = {};
  actionEditAssignees: Record<number, BasicUser | null> = {};
  actionFieldErrors: Record<number, { title?: string; due?: string; owner?: string; audit?: string }> = {};
  actionEdits: Record<number, WorkflowActionTemplate> = {};
  pendingUpdates: { workflowId: number; action: WorkflowActionTemplate }[] = [];
  pendingDeletes: { workflowId: number; actionId: number }[] = [];
  pendingCreates: { workflowId: number; request: CreateWorkflowActionRequest; tempId: number }[] = [];
  actionsDirty: boolean = false;
  actionCounts: Record<number, number> = {};
  mandatoryCounts: Record<number, number> = {};
  editedWorkflows: Record<number, WorkflowTemplate> = {};
  pendingWorkflowUpdates: WorkflowTemplate[] = [];
  pendingWorkflowDeletes: number[] = [];
  workflowsDirty: boolean = false;
  private tempActionId: number = -1;
  users: BasicUser[] = [];
  loadingUsers: boolean = false;

  constructor(
    private workflowService: WorkflowService,
    private toast: ToastrService,
    private complianceActionService: ComplianceActionService,
    private userHelperService: UserService,
    private confirmationService: ConfirmationService
  ){}

  private workflowError(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  ngOnInit(){
    this.loadUsers();
    this.loadWorkflows();
  }

  private loadUsers(){
    this.loadingUsers = true;
    const req = new ActionUserRequest({ EventID: -1, LocationID: -1 } as any);
    this.complianceActionService.getActionUsers(req).subscribe({
      next: list => {
        this.loadingUsers = false;
        this.users = this.userHelperService.sortUsers(list || []);
        if (!this.users.some(u => u.UserID === -1)){
          const placeholder = new BasicUser({ UserID: -1, DisplayName: 'Please Select...', Seq: 0, UserTypeID: 0 }) as any;
          (placeholder as any).Disabled = true;
          this.users.unshift(placeholder);
        }
      },
      error: () => {
        this.loadingUsers = false;
        this.users = [];
      }
    });
  }

  loadWorkflows(){
    this.loading = true;
    this.errorMessage = '';
    this.workflowService.getWorkflows().subscribe({
      next: list => {
        this.workflows = list || [];
        this.loading = false;
        this.workflows.forEach(w => {
          this.actionCounts[w.WorkflowID] = w.ActionCount || 0;
          this.mandatoryCounts[w.WorkflowID] = w.MandatoryActionCount || 0;
        });
      },
      error: error => {
        this.loading = false;
        this.errorMessage = this.workflowError(error, 'Unable to load workflows right now.');
      }
    });
  }

  get hasUnsavedChanges(): boolean{
    return !!(this.newWorkflowName?.trim() || this.newWorkflowDescription?.trim() || this.actionsDirty || this.workflowsDirty);
  }

  onSaveRequested(){
    if (this.saving){
      return;
    }
    if (this.actionsDirty || this.workflowsDirty){
      this.saveAllChanges();
    } else if (this.hasUnsavedChanges){
      this.createWorkflow();
    }
  }

  onUndoRequested(){
    if (this.saving || !this.hasUnsavedChanges){
      return;
    }
    this.resetForm();
    this.resetActionChanges();
    this.loadWorkflows();
  }

  private resetForm(){
    this.newWorkflowName = '';
    this.newWorkflowDescription = '';
    this.formError = '';
  }

  private resetActionChanges(){
    this.actions = {};
    this.actionsLoading = {};
    this.actionsError = {};
    this.actionForms = {};
    this.actionAssignees = {};
    this.actionFieldErrors = {};
    this.actionEdits = {};
    this.actionCounts = {};
    this.mandatoryCounts = {};
    this.expandedWorkflowId = -1;
    this.pendingUpdates = [];
    this.pendingDeletes = [];
    this.pendingCreates = [];
    this.actionsDirty = false;
    this.pendingWorkflowUpdates = [];
    this.pendingWorkflowDeletes = [];
    this.workflowsDirty = false;
    this.editedWorkflows = {};
  }

  private recalculateCounts(workflowId: number){
    const list = this.actions[workflowId] || [];
    this.actionCounts[workflowId] = list.length;
    this.mandatoryCounts[workflowId] = list.filter(a => a.IsMandatory).length;
  }

  toggleActions(workflow: WorkflowTemplate){
    if (!workflow?.WorkflowID || workflow.WorkflowID === -1){
      return;
    }
    if (this.expandedWorkflowId === workflow.WorkflowID){
      this.expandedWorkflowId = -1;
      return;
    }
    this.expandedWorkflowId = workflow.WorkflowID;
    if (!this.actionForms[workflow.WorkflowID]){
      this.actionForms[workflow.WorkflowID] = new CreateWorkflowActionRequest({ WorkflowID: workflow.WorkflowID, ActionVariationID: 1 });
    }
    if (!this.actions[workflow.WorkflowID]){
      this.loadActions(workflow.WorkflowID);
    }
  }

  private loadActions(workflowId: number){
    this.actionsLoading[workflowId] = true;
    this.actionsError[workflowId] = '';
    this.workflowService.getWorkflowActions(workflowId).subscribe({
      next: list => {
        this.actions[workflowId] = list || [];
        this.actionsLoading[workflowId] = false;
        if (!this.actionForms[workflowId]){
          this.actionForms[workflowId] = new CreateWorkflowActionRequest({ WorkflowID: workflowId, ActionVariationID: 1 });
        }
        if (!this.actionAssignees[workflowId]){
          this.actionAssignees[workflowId] = this.users.find(u => u.UserID === -1) || null;
        }
        (this.actions[workflowId] || []).forEach(a => {
          if (!a.ActionVariationID){
            a.ActionVariationID = 1;
          }
        });
        (this.actions[workflowId] || []).forEach(a => {
          if (!this.actionEditAssignees[a.WorkflowActionID]){
            this.actionEditAssignees[a.WorkflowActionID] = this.users.find(u => u.UserID === a.DefaultOwnerUserID) || null;
          }
        });
        this.recalculateCounts(workflowId);
      },
      error: error => {
        this.actionsLoading[workflowId] = false;
        this.actionsError[workflowId] = this.workflowError(error, 'Unable to load actions.');
      }
    });
  }

  addWorkflowAction(workflowId: number){
    const form = this.actionForms[workflowId] || new CreateWorkflowActionRequest({ WorkflowID: workflowId });
    const title = form.Title?.trim();
    const errors: { title?: string; due?: string; owner?: string; audit?: string } = {};
    if (!title){
      errors.title = 'Please enter an action title.';
    }
    if (form.DefaultDueInDays === null || form.DefaultDueInDays === undefined){
      errors.due = 'Please enter due in days.';
    } else if (form.DefaultDueInDays < 0){
      errors.due = 'Due in days cannot be negative.';
    }
    const assignee = this.actionAssignees[workflowId];
    if (!assignee || assignee.UserID === -1){
      errors.owner = 'Please select an owner.';
    }
    if ((form.ActionVariationID ?? 1) === 2){
      if (!form.AuditID || form.AuditID <= 0){
        errors.audit = this.customerContext.displayText('Please select the required audit.');
      }
    } else {
      form.AuditID = null;
    }
    this.actionFieldErrors[workflowId] = errors;
    this.actionsError[workflowId] = '';
    if (errors.title || errors.due || errors.owner || errors.audit){
      this.actionsError[workflowId] = 'Please fix the highlighted fields.';
      return;
    }
    this.actionSaving[workflowId] = true;
    form.DefaultOwnerUserID = assignee && assignee.UserID !== -1 ? assignee.UserID : null;
    form.DefaultOwnerTypeID = assignee && assignee.UserID !== -1 ? assignee.UserTypeID : null;
    const tempId = this.tempActionId--;
    const staged = new WorkflowActionTemplate({
      WorkflowActionID: tempId,
      WorkflowID: workflowId,
      Title: form.Title?.trim() || '',
      Description: form.Description || '',
      DefaultDueInDays: form.DefaultDueInDays ?? null as any,
      IsMandatory: form.IsMandatory,
      DefaultOwnerUserID: form.DefaultOwnerUserID ?? undefined,
      DefaultOwnerTypeID: form.DefaultOwnerTypeID ?? undefined,
      ActionVariationID: form.ActionVariationID ?? 1,
      AuditID: form.AuditID ?? null
    });
    this.pendingCreates.push({ workflowId, request: { ...form }, tempId });
    this.actions[workflowId] = [staged, ...(this.actions[workflowId] || [])];
    this.actionSaving[workflowId] = false;
    this.actionForms[workflowId] = new CreateWorkflowActionRequest({ WorkflowID: workflowId, ActionVariationID: 1 });
    this.actionAssignees[workflowId] = this.users.find(u => u.UserID === -1) || null;
    this.actionFieldErrors[workflowId] = {};
    this.actionsDirty = true;
    this.recalculateCounts(workflowId);
  }

  startEdit(action: WorkflowActionTemplate){
    this.actionEdits[action.WorkflowActionID] = { ...action };
    if (!this.actionEditAssignees[action.WorkflowActionID]){
      this.actionEditAssignees[action.WorkflowActionID] = this.users.find(u => u.UserID === action.DefaultOwnerUserID) || this.users.find(u => u.UserID === -1) || null;
    }
  }

  cancelEdit(actionId: number){
    delete this.actionEdits[actionId];
    delete this.actionEditAssignees[actionId];
  }

  confirmEdit(workflowId: number, actionId: number){
    const edit = this.actionEdits[actionId];
    if (!edit){ return; }
    // reuse validation
    const errors: { title?: string; due?: string; owner?: string; audit?: string } = {};
    if (!edit.Title || !edit.Title.trim()){
      errors.title = 'Please enter an action title.';
    }
    if (edit.DefaultDueInDays === null || edit.DefaultDueInDays === undefined){
      errors.due = 'Please enter due in days.';
    } else if (edit.DefaultDueInDays < 0){
      errors.due = 'Due in days cannot be negative.';
    }
    const selected = this.actionEditAssignees[actionId];
    if (!selected || selected.UserID === -1){
      errors.owner = 'Please select an owner.';
    }
    this.actionFieldErrors[workflowId] = errors;
    if ((edit.ActionVariationID ?? 1) === 2){
      if (!edit.AuditID || edit.AuditID <= 0){
        errors.audit = this.customerContext.displayText('Please select the required audit.');
      }
    } else {
      edit.AuditID = null;
    }
    if (errors.title || errors.due || errors.owner || errors.audit){
      this.actionsError[workflowId] = 'Please fix the highlighted fields.';
      return;
    }
    edit.DefaultOwnerUserID = selected?.UserID ?? null as any;
    edit.DefaultOwnerTypeID = selected?.UserTypeID ?? null as any;
    // stage update
    this.pendingUpdates = this.pendingUpdates.filter(p => p.action.WorkflowActionID !== actionId);
    this.pendingUpdates.push({ workflowId, action: { ...edit } });
    const list = this.actions[workflowId] || [];
    const idx = list.findIndex(a => a.WorkflowActionID === actionId);
    if (idx >= 0){
      list[idx] = { ...edit };
      this.actions[workflowId] = [...list];
    }
    if (actionId < 0){
      this.pendingCreates = this.pendingCreates.map(c => c.tempId === actionId ? { ...c, request: new CreateWorkflowActionRequest({
        WorkflowID: workflowId,
        Title: edit.Title,
        Description: edit.Description,
        DefaultDueInDays: edit.DefaultDueInDays,
        IsMandatory: edit.IsMandatory,
        DefaultOwnerUserID: edit.DefaultOwnerUserID ?? undefined,
        DefaultOwnerTypeID: edit.DefaultOwnerTypeID ?? undefined,
        ActionVariationID: edit.ActionVariationID ?? 1,
        AuditID: edit.AuditID ?? null
      }) } : c);
    } else {
      this.pendingUpdates = this.pendingUpdates.filter(p => p.action.WorkflowActionID !== actionId);
      this.pendingUpdates.push({ workflowId, action: { ...edit } });
    }
    delete this.actionEdits[actionId];
    delete this.actionEditAssignees[actionId];
    this.actionsDirty = true;
    this.actionsError[workflowId] = '';
    this.recalculateCounts(workflowId);
  }

  markDelete(workflowId: number, actionId: number){
    const list = this.actions[workflowId] || [];
    this.actions[workflowId] = list.filter(a => a.WorkflowActionID !== actionId);
    if (actionId < 0){
      this.pendingCreates = this.pendingCreates.filter(c => c.tempId !== actionId);
    } else {
      this.pendingDeletes = [...this.pendingDeletes.filter(d => d.actionId !== actionId), { workflowId, actionId }];
    }
    this.actionsDirty = true;
    this.recalculateCounts(workflowId);
  }

  startWorkflowEdit(wf: WorkflowTemplate){
    this.editedWorkflows[wf.WorkflowID] = { ...wf };
  }

  cancelWorkflowEdit(id: number){
    delete this.editedWorkflows[id];
  }

  confirmWorkflowEdit(id: number){
    const edit = this.editedWorkflows[id];
    if (!edit){ return; }
    const name = edit.Name?.trim();
    if (!name){
      this.errorMessage = 'Workflow name is required.';
      return;
    }
    this.pendingWorkflowUpdates = this.pendingWorkflowUpdates.filter(u => u.WorkflowID !== id);
    this.pendingWorkflowUpdates.push({ ...edit, Name: name });
    const idx = this.workflows.findIndex(w => w.WorkflowID === id);
    if (idx !== -1){
      this.workflows[idx] = { ...edit, Name: name };
    }
    delete this.editedWorkflows[id];
    this.workflowsDirty = true;
    this.errorMessage = '';
  }

  deleteWorkflow(id: number){
    this.workflows = this.workflows.filter(w => w.WorkflowID !== id);
    this.pendingWorkflowDeletes = [...this.pendingWorkflowDeletes.filter(d => d !== id), id];
    this.workflowsDirty = true;
  }

  private saveAllChanges(){
    const actionCreates = this.pendingCreates.map(c => this.workflowService.createWorkflowAction(c.workflowId, c.request));
    const actionUpdates = this.pendingUpdates.map(u => this.workflowService.updateWorkflowAction(new UpdateWorkflowActionRequest({
      WorkflowID: u.workflowId,
      WorkflowActionID: u.action.WorkflowActionID,
      Title: u.action.Title,
      Description: u.action.Description,
      DefaultDueInDays: u.action.DefaultDueInDays,
      IsMandatory: u.action.IsMandatory,
      DefaultOwnerUserID: u.action.DefaultOwnerUserID,
      DefaultOwnerTypeID: u.action.DefaultOwnerTypeID,
      ActionVariationID: u.action.ActionVariationID ?? 1,
      AuditID: u.action.AuditID ?? null
    })));
    const actionDeletes = this.pendingDeletes.map(d => this.workflowService.deleteWorkflowAction({ WorkflowID: d.workflowId, WorkflowActionID: d.actionId }));
    const workflowUpdates = this.pendingWorkflowUpdates.map(w => this.workflowService.updateWorkflow(w.WorkflowID, new CreateWorkflowRequest({ Name: w.Name, Description: w.Description })));
    const workflowDeletes = this.pendingWorkflowDeletes.map(id => this.workflowService.deleteWorkflow(id));
    const all = [...actionCreates, ...actionUpdates, ...actionDeletes, ...workflowUpdates, ...workflowDeletes];
    if (all.length === 0){
      this.actionsDirty = false;
      this.workflowsDirty = false;
      return;
    }
    const impactedWorkflows = new Set<number>();
    this.pendingCreates.forEach(c => impactedWorkflows.add(c.workflowId));
    this.pendingUpdates.forEach(u => impactedWorkflows.add(u.workflowId));
    this.pendingDeletes.forEach(d => impactedWorkflows.add(d.workflowId));
    this.pendingWorkflowUpdates.forEach(w => impactedWorkflows.add(w.WorkflowID));
    this.pendingWorkflowDeletes.forEach(id => impactedWorkflows.add(id));
    this.saving = true;
    import('rxjs').then(rx => {
      rx.forkJoin(all as any).subscribe({
        next: () => {
          this.saving = false;
          this.actionsDirty = false;
          this.workflowsDirty = false;
          this.pendingCreates = [];
          this.pendingUpdates = [];
          this.pendingDeletes = [];
          this.pendingWorkflowDeletes = [];
          this.pendingWorkflowUpdates = [];
          this.toast.addToastr(new Toastr({ Type: 2, Message: 'Changes saved.' }));
          impactedWorkflows.forEach(id => {
            if (this.pendingWorkflowDeletes.includes(id)){
              return;
            }
            this.loadActions(id);
          });
          this.loadWorkflows();
        },
        error: error => {
          this.saving = false;
          this.toast.addToastr(new Toastr({ Type: 3, Message: this.workflowError(error, 'Unable to save changes.') }));
        }
      });
    });
  }

  confirmDeleteWorkflow(event: Event, workflowId: number){
    const target = event.currentTarget as HTMLElement | null;
    if (!target){ return; }
    this.confirmationService.confirm({
      target,
      key: 'workflowConfirm',
      header: 'Delete workflow',
      message: 'Are you sure you want to delete this workflow?',
      defaultFocus: 'reject',
      accept: () => this.deleteWorkflow(workflowId),
      reject: () => {}
    });
  }

  confirmDeleteAction(event: Event, workflowId: number, actionId: number){
    const target = event.currentTarget as HTMLElement | null;
    if (!target){ return; }
    this.confirmationService.confirm({
      target,
      key: 'workflowConfirm',
      header: 'Delete action',
      message: 'Are you sure you want to delete this action?',
      defaultFocus: 'reject',
      accept: () => this.markDelete(workflowId, actionId),
      reject: () => {}
    });
  }

  onConfirmAccept(confirmation: any){
    confirmation?.acceptEvent?.emit();
    this.confirmationService.close();
  }

  onConfirmReject(confirmation: any){
    confirmation?.rejectEvent?.emit();
    this.confirmationService.close();
  }

  actionsFor(id: number): WorkflowActionTemplate[] {
    const list = this.actions[id] || [];
    return [...list].sort((a,b) => {
      const aDue = a.DefaultDueInDays ?? Number.MAX_SAFE_INTEGER;
      const bDue = b.DefaultDueInDays ?? Number.MAX_SAFE_INTEGER;
      if (aDue !== bDue){
        return aDue - bDue;
      }
      return (a.Seq ?? 0) - (b.Seq ?? 0);
    });
  }

  totalActions(id: number): number{
    if (this.actionCounts[id] !== undefined){
      return this.actionCounts[id];
    }
    return this.actionsFor(id).length;
  }

  mandatoryActions(id: number): number{
    if (this.mandatoryCounts[id] !== undefined){
      return this.mandatoryCounts[id];
    }
    return this.actionsFor(id).filter(a => a.IsMandatory).length;
  }

  actionWord(count?: number | null): string{
    const value = count ?? 0;
    return value === 1 ? 'action' : 'actions';
  }

  defaultOwnerName(action: WorkflowActionTemplate): string{
    if (!action?.DefaultOwnerUserID && action?.DefaultOwnerUserID !== 0){
      return '';
    }
    const user = this.users.find(u => u.UserID === action.DefaultOwnerUserID);
    return user?.DisplayName || '';
  }

  createWorkflow(){
    this.formError = '';
    const name = this.newWorkflowName?.trim() || '';
    const description = this.newWorkflowDescription?.trim() || '';
    if (!name){
      this.formError = 'Please enter a workflow name.';
      return;
    }
    const request = new CreateWorkflowRequest({ Name: name, Description: description });
    this.saving = true;
    this.workflowService.createWorkflow(request).subscribe({
      next: workflow => {
        this.saving = false;
        this.workflows = workflow ? [workflow, ...this.workflows] : [...this.workflows];
        this.resetForm();
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Workflow created.' }));
      },
      error: error => {
        this.saving = false;
        this.formError = this.workflowError(error, 'Unable to create workflow right now.');
      }
    });
  }
}
